import { forwardRef, useEffect, useRef, useCallback } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme, useId, ModalIdContext } from "@xsolla/xui-core";
import { FlexButton, getFlexButtonBoxSize } from "@xsolla/xui-button";
import { ChevronLeft, Remove } from "@xsolla/xui-icons-base";
import type { ModalFooterAlign, ModalProps } from "./types";
import { WorkArea } from "./WorkArea";

const FOCUSABLE_SELECTORS =
  'button:not([disabled]), [href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * `footerAlign` -> the flex `align-items` value on the footer wrapper.
 * `stretch` is the default and matches the wrapper's original behaviour, so
 * existing modals render identically. See FEP-755.
 */
const FOOTER_ALIGN_ITEMS: Record<ModalFooterAlign, string> = {
  stretch: "stretch",
  start: "flex-start",
  center: "center",
  end: "flex-end",
};

const isElementVisible = (el: HTMLElement): boolean => {
  if (el.offsetParent === null && getComputedStyle(el).position !== "fixed")
    return false;
  const style = getComputedStyle(el);
  return style.visibility !== "hidden" && style.display !== "none";
};

const getFocusableElements = (container: HTMLElement): HTMLElement[] =>
  Array.from(
    container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS)
  ).filter(isElementVisible);

export const Modal = forwardRef<any, ModalProps>(
  (
    {
      children,
      type = "popup",
      openContent = false,
      closeOutside = true,
      backgroundColor,
      onClose,
      onBack,
      align,
      heading,
      subheading,
      header,
      footer,
      footerAlign = "stretch",
      styled: styledProps,
      maxWidth,
      minHeight,
      title,
      "aria-label": ariaLabel,
      "aria-describedby": ariaDescribedBy,
      initialFocusRef,
      testID,
      closeButtonTestId,
      closeButtonSize = "xl",
      themeMode,
      themeProductContext,
      ...rest
    },
    ref
  ) => {
    const { theme } = useResolvedTheme({ themeMode, themeProductContext });
    const sizing = theme.sizing.modal();
    // Reserved header columns, sourced from FlexButton so they can't desync.
    const headerButtonBox = getFlexButtonBoxSize(closeButtonSize);
    const modalRef = useRef<HTMLDivElement>(null);
    const closeButtonRef = useRef<HTMLButtonElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);
    const titleId = useId();

    useEffect(() => {
      previousActiveElement.current = document.activeElement as HTMLElement;

      const focusTarget =
        initialFocusRef?.current ||
        closeButtonRef.current ||
        (modalRef.current && getFocusableElements(modalRef.current)[0]);

      if (focusTarget) {
        focusTarget.focus();
      } else {
        modalRef.current?.focus();
      }

      return () => {
        previousActiveElement.current?.focus();
      };
    }, [initialFocusRef]);

    useEffect(() => {
      if (!onClose) return;
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") onClose();
      };
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onClose]);

    useEffect(() => {
      if (!closeOutside || !onClose) return;
      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target;
        if (!target || !(target instanceof Node)) return;
        if (modalRef.current && !modalRef.current.contains(target)) {
          const portalContent = (target as Element).closest?.(
            `[data-modal-id="${titleId}"]`
          );
          if (!portalContent) {
            onClose();
          }
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, [closeOutside, onClose, titleId]);

    const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
      if (event.key !== "Tab" || !modalRef.current) return;

      const focusableElements = getFocusableElements(modalRef.current);
      const firstElement = focusableElements[0];
      const lastElement = focusableElements[focusableElements.length - 1];

      if (!firstElement) {
        event.preventDefault();
        return;
      }

      if (event.shiftKey && document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      } else if (!event.shiftKey && document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }, []);

    const maxWidthValue =
      type === "full-screen" || type === "bottom-sheet"
        ? "100%"
        : (maxWidth ?? sizing.maxWidth);
    const hasDefaultHeader =
      !header && (onBack || onClose || heading || subheading);
    const hasHeader = !!header || hasDefaultHeader;

    const typeStyles: React.CSSProperties =
      type === "full-screen"
        ? {
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1,
            maxWidth: "100%",
          }
        : type === "bottom-sheet"
          ? {
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              zIndex: 1,
              maxWidth: "100%",
            }
          : {};

    return (
      <Box
        testID={testID}
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        aria-describedby={ariaDescribedBy}
        data-modal-id={titleId}
        data-modal-type={type}
        tabIndex={-1}
        onKeyDown={handleKeyDown}
        flexGrow={1}
        position="relative"
        width="100%"
        style={{
          maxWidth:
            typeof maxWidthValue === "number"
              ? `${maxWidthValue}px`
              : maxWidthValue,
          minHeight:
            typeof minHeight === "number" ? `${minHeight}px` : minHeight,
          outline: "none",
          ...typeStyles,
          ...styledProps,
        }}
        {...rest}
      >
        {title && (
          <Box
            id={titleId}
            position="absolute"
            style={{
              clip: "rect(0 0 0 0)",
              clipPath: "inset(50%)",
              height: 1,
              width: 1,
              overflow: "hidden",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </Box>
        )}
        <ModalIdContext.Provider value={titleId}>
          <WorkArea
            ref={ref}
            stretched
            openContent={openContent}
            type={type}
            align={align}
            backgroundColor={backgroundColor}
          >
            {hasHeader && (
              <Box
                width="100%"
                padding={sizing.headerPadding}
                style={{ flexShrink: 0 }}
              >
                <Box
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems="center"
                  width="100%"
                  style={{ minHeight: sizing.headerMinHeight, gap: 8 }}
                >
                  {header ?? (
                    <>
                      <Box style={{ minWidth: headerButtonBox }}>
                        {onBack && (
                          <FlexButton
                            variant="secondary"
                            size={closeButtonSize}
                            background
                            iconLeft={<ChevronLeft />}
                            onPress={onBack}
                            aria-label="Go back"
                            themeMode={themeMode}
                            themeProductContext={themeProductContext}
                            testID="modal-back-button"
                          />
                        )}
                      </Box>
                      {(heading || subheading) && (
                        <Box
                          flexGrow={1}
                          flexDirection="column"
                          alignItems="center"
                          justifyContent="center"
                          style={{ gap: 2, minWidth: 0, overflow: "hidden" }}
                        >
                          {heading && (
                            <Text
                              color={theme.colors.content.primary}
                              fontSize={
                                theme.typographyTokens.basic["body-lg"].fontSize
                              }
                              lineHeight={
                                theme.typographyTokens.basic["body-lg"]
                                  .lineHeight
                              }
                              fontWeight={
                                theme.typographyTokens.basic["body-lg"].accent
                                  ?.fontWeight ?? 500
                              }
                              fontFamily={theme.fonts.body}
                              whiteSpace="nowrap"
                              textAlign="center"
                              style={{
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {heading}
                            </Text>
                          )}
                          {subheading && (
                            <Text
                              color={theme.colors.content.secondary}
                              fontSize={
                                theme.typographyTokens.basic["body-sm"].fontSize
                              }
                              lineHeight={
                                theme.typographyTokens.basic["body-sm"]
                                  .lineHeight
                              }
                              fontWeight={
                                theme.typographyTokens.basic["body-sm"]
                                  .fontWeight
                              }
                              fontFamily={theme.fonts.body}
                              whiteSpace="nowrap"
                              textAlign="center"
                              style={{
                                maxWidth: "100%",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {subheading}
                            </Text>
                          )}
                        </Box>
                      )}
                      <Box
                        style={{ minWidth: headerButtonBox }}
                        alignItems="flex-end"
                      >
                        {onClose && (
                          <FlexButton
                            ref={closeButtonRef}
                            variant="secondary"
                            size={closeButtonSize}
                            background
                            iconLeft={<Remove />}
                            onPress={onClose}
                            aria-label="Close modal"
                            themeMode={themeMode}
                            themeProductContext={themeProductContext}
                            // Keeps this out of FlexButton's "flex-button" fallback.
                            testID={closeButtonTestId ?? "modal-close-button"}
                          />
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
            <Box
              flexGrow={1}
              width="100%"
              padding={openContent ? 0 : sizing.contentPadding}
              style={{
                paddingTop:
                  hasHeader && !openContent ? sizing.headerGap : undefined,
                paddingBottom:
                  footer && !openContent ? sizing.footerGap : undefined,
              }}
            >
              {children}
            </Box>
            {footer && (
              <Box
                width="100%"
                padding={openContent ? 0 : sizing.contentPadding}
                alignItems={FOOTER_ALIGN_ITEMS[footerAlign]}
                style={{ paddingTop: 0, flexShrink: 0 }}
              >
                {footer}
              </Box>
            )}
          </WorkArea>
        </ModalIdContext.Provider>
      </Box>
    );
  }
);

Modal.displayName = "Modal";
